import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../hooks/useChat';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from '../../components/AppIcon';
import CitySelector from './components/CitySelector';
import BDGenerationStatus from './components/BDGenerationStatus';
import BDExecutiveSummary from './components/BDExecutiveSummary';
import BDCityMetricsDashboard from './components/BDCityMetricsDashboard';
import BDReportSections from './components/BDReportSections';
import BDHistoricalReports from './components/BDHistoricalReports';
import BDExportReport from './components/BDExportReport';

const CITY_LISTING_DATA = {
  all: {
    totalListings: 24500,
    avgPrice: '৳1,20,00,000',
    topAreas: ['Gulshan', 'Banani', 'Dhanmondi', 'Agrabad', 'Zindabazar'],
  },
  dhaka: {
    totalListings: 12800,
    avgPrice: '৳1,45,00,000',
    topAreas: ['Gulshan', 'Banani', 'Dhanmondi', 'Uttara', 'Bashundhara R/A'],
  },
  chittagong: {
    totalListings: 5200,
    avgPrice: '৳85,00,000',
    topAreas: ['Agrabad', 'Nasirabad', 'Halishahar', 'Panchlaish', 'GEC Circle'],
  },
  sylhet: {
    totalListings: 2900,
    avgPrice: '৳75,00,000',
    topAreas: ['Zindabazar', 'Amberkhana', 'Shahjalal Upashahar', 'Subhanighat', 'Tilagarh'],
  },
  rajshahi: {
    totalListings: 1800,
    avgPrice: '৳45,00,000',
    topAreas: ['Shaheb Bazar', 'Uposhahar', 'Kazla', 'Binodpur', 'Rajpara'],
  },
  khulna: {
    totalListings: 1700,
    avgPrice: '৳50,00,000',
    topAreas: ['Sonadanga', 'Khalishpur', 'Boyra', 'Nirala', 'Rupsha'],
  },
};

const buildSystemPrompt = (city, period, listingData) => {
  const cityName = city === 'all' ? 'All Major Bangladesh Cities (Dhaka, Chittagong, Sylhet)' : city?.replace(/-/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
  const data = listingData?.[city] || listingData?.all;
  return `You are a Bangladesh real estate market intelligence analyst specializing in Dhaka, Chittagong, and Sylhet property markets. Generate a comprehensive monthly market intelligence report for ${cityName} for the period ${period}.

Real listing data context:
- Total active listings: ${data?.totalListings?.toLocaleString()}
- Average listing price: ${data?.avgPrice} BDT
- Top areas: ${data?.topAreas?.join(', ')}

Key Bangladesh market context:
- Currency: BDT (Bangladeshi Taka ৳), prices in Lakh (লাখ) and Crore (কোটি)
- Regulatory body: RAJUK (Rajdhani Unnayan Kartripakkha) for Dhaka; CDA for Chittagong
- NRB (Non-Resident Bangladeshi) diaspora investment is significant, especially in Sylhet
- Key investment areas: Purbachal New Town, Uttara 3rd Phase, Bashundhara R/A, Jhilmil
- Dhaka: Gulshan, Banani, Dhanmondi are premium; Mirpur, Mohammadpur are mid-market
- Chittagong: Agrabad commercial hub, Nasirabad residential, Halishahar industrial
- Sylhet: Strong UK/US diaspora investment, premium villa developments

Generate a detailed JSON report with this EXACT structure (no markdown, pure JSON):
{
  "executiveSummary": "3-4 paragraph executive summary covering key market trends, price movements in BDT, and outlook for Bangladesh property market",
  "keyHighlights": ["highlight 1", "highlight 2", "highlight 3", "highlight 4", "highlight 5"],
  "metrics": {
    "medianPrice": "৳X কোটি / ৳X লাখ",
    "medianPriceTrend": "+X.X%",
    "yoyGrowth": "+X.X%",
    "transactions": "X,XXX",
    "transactionsTrend": "+X%",
    "pricePerSqft": "৳X,XXX",
    "pricePerSqftTrend": "+X.X%"
  },
  "cityMetrics": [
    {"name": "Dhaka", "yoyGrowth": 7.8, "medianPriceSqft": 8500},
    {"name": "Chittagong", "yoyGrowth": 6.2, "medianPriceSqft": 5200},
    {"name": "Sylhet", "yoyGrowth": 9.1, "medianPriceSqft": 4800},
    {"name": "Rajshahi", "yoyGrowth": 4.5, "medianPriceSqft": 3200},
    {"name": "Khulna", "yoyGrowth": 3.8, "medianPriceSqft": 3500}
  ],
  "supplyDemand": [
    {"month": "Aug", "supply": 68, "demand": 82},
    {"month": "Sep", "supply": 72, "demand": 85},
    {"month": "Oct", "supply": 75, "demand": 88},
    {"month": "Nov", "supply": 78, "demand": 91},
    {"month": "Dec", "supply": 74, "demand": 87},
    {"month": "Jan", "supply": 80, "demand": 94}
  ],
  "investmentHotspots": [
    {"area": "Gulshan-2, Dhaka", "roi": "+X.X% ROI", "score": 91},
    {"area": "Purbachal New Town", "roi": "+X.X% ROI", "score": 87},
    {"area": "Agrabad, Chittagong", "roi": "+X.X% ROI", "score": 82},
    {"area": "Shahjalal Upashahar, Sylhet", "roi": "+X.X% ROI", "score": 78},
    {"area": "Bashundhara R/A, Dhaka", "roi": "+X.X% ROI", "score": 74}
  ],
  "marketAnalysis": {
    "summary": "Detailed market analysis paragraph covering Bangladesh property market dynamics",
    "pricingForecasts": [
      {"segment": "Premium Apartments (Gulshan/Banani)", "forecast": "+X%", "analysis": "Brief analysis"},
      {"segment": "Mid-Market Apartments (Mirpur/Mohammadpur)", "forecast": "+X%", "analysis": "Brief analysis"},
      {"segment": "Affordable Housing (Keraniganj/Savar)", "forecast": "+X%", "analysis": "Brief analysis"},
      {"segment": "Commercial Properties", "forecast": "+X%", "analysis": "Brief analysis"}
    ]
  },
  "rajukUpdates": {
    "summary": "Summary of RAJUK regulatory environment and compliance requirements",
    "changes": [
      {"type": "New", "title": "RAJUK regulation title", "description": "Description of new rule", "effectiveDate": "Month YYYY"},
      {"type": "Update", "title": "Updated regulation", "description": "Description of update", "effectiveDate": "Month YYYY"},
      {"type": "Reminder", "title": "Compliance reminder", "description": "Important reminder for buyers/developers", "effectiveDate": "Month YYYY"}
    ]
  },
  "newDevelopments": {
    "marketOverview": "Overview of new residential and commercial development launches in Bangladesh",
    "projects": [
      {"projectName": "Project Name", "developer": "Developer Name", "location": "Dhaka/Chittagong/Sylhet area", "startingPrice": "৳X কোটি", "handover": "Q4 2027", "highlights": "Key features", "rajukApproved": true},
      {"projectName": "Project Name", "developer": "Developer Name", "location": "Location", "startingPrice": "৳X লাখ", "handover": "Q2 2028", "highlights": "Key features", "rajukApproved": true},
      {"projectName": "Project Name", "developer": "Developer Name", "location": "Location", "startingPrice": "৳X কোটি", "handover": "Q1 2027", "highlights": "Key features", "rajukApproved": false}
    ]
  },
  "expatTrends": {
    "summary": "Summary of NRB (Non-Resident Bangladeshi) buying trends and remittance-linked investment patterns in Dhaka, Chittagong, and Sylhet",
    "topDiaspora": [
      {"country": "United Kingdom", "share": "32%"},
      {"country": "United States", "share": "22%"},
      {"country": "Middle East", "share": "28%"},
      {"country": "Italy/Europe", "share": "10%"}
    ],
    "remittanceInsights": [
      "Insight about NRB investment patterns in Sylhet premium villas",
      "Insight about UK/US diaspora preference for Dhaka premium apartments",
      "Insight about remittance flow impact on property prices",
      "Insight about NRB-specific financing options and legal requirements"
    ]
  }
}`;
};

const parseReportJSON = (text) => {
  try {
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (jsonMatch) return JSON.parse(jsonMatch?.[0]);
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const PERIOD_LABELS = {
  '2025-02': 'February 2025',
  '2025-01': 'January 2025',
  '2024-12': 'December 2024',
  '2024-11': 'November 2024',
  '2024-10': 'October 2024',
  '2024-09': 'September 2024',
};

const BangladeshMarketIntelligenceReports = () => {
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('2025-02');
  const [report, setReport] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCity, setGeneratedCity] = useState('');
  const [generatedPeriod, setGeneratedPeriod] = useState('');
  const navigate = useNavigate();

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    if (error) toast?.error('Failed to generate report. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading && isGenerating) {
      const parsed = parseReportJSON(response);
      if (parsed) {
        setReport(parsed);
        setIsGenerating(false);
        toast?.success('Bangladesh market intelligence report generated successfully!');
      } else {
        setIsGenerating(false);
        toast?.error('Could not parse report data. Please try again.');
      }
    }
  }, [response, isLoading]);

  const handleGenerate = useCallback(() => {
    setIsGenerating(true);
    setReport(null);
    setGeneratedCity(selectedCity);
    setGeneratedPeriod(selectedPeriod);

    const periodLabel = PERIOD_LABELS?.[selectedPeriod] || 'February 2025';
    const systemPrompt = buildSystemPrompt(selectedCity, periodLabel, CITY_LISTING_DATA);

    sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Generate the complete Bangladesh market intelligence report for ${selectedCity === 'all' ? 'All Major Cities (Dhaka, Chittagong, Sylhet)' : selectedCity} for ${periodLabel}. Return only valid JSON.` },
    ], { max_completion_tokens: 4000 });
  }, [selectedCity, selectedPeriod, sendMessage]);

  const handleLoadHistorical = (r) => {
    toast(`Loading ${r?.period} report...`, { icon: '📂' });
  };

  const cityName = generatedCity === 'all' ? 'Dhaka / Chittagong / Sylhet' : generatedCity?.replace(/-/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
  const periodLabel = PERIOD_LABELS?.[generatedPeriod] || '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-gray-500 hover:text-green-600 transition-colors mr-1 group"
              aria-label="Back to Home"
            >
              <Icon name="ArrowLeft" size={18} className="group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="w-px h-6 bg-gray-200" />
            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
              <span className="text-lg">🇧🇩</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Bangladesh Market Intelligence Reports</h1>
              <p className="text-sm text-gray-500">AI-powered monthly analysis — Dhaka · Chittagong · Sylhet</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-green-700">Powered by OpenAI</span>
            </div>
          </div>
          <CitySelector
            selectedCity={selectedCity}
            onCityChange={setSelectedCity}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            onGenerate={handleGenerate}
            isGenerating={isGenerating || isLoading}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Main Report Area */}
          <div className="xl:col-span-3">
            <BDGenerationStatus
              isGenerating={isGenerating || isLoading}
              city={cityName}
              period={periodLabel}
            />

            {!report && !isGenerating && !isLoading && (
              <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-12 text-center">
                <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🇧🇩</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate Your Bangladesh Market Report</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                  Select a city and report period above, then click "Generate New Report" to create a comprehensive AI-powered market intelligence report for Bangladesh real estate.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['Dhaka Analysis', 'Chittagong Trends', 'Sylhet NRB Market', 'RAJUK Compliance', 'Expat Insights']?.map((tag) => (
                    <span key={tag} className="bg-green-50 text-green-600 text-xs font-medium px-3 py-1 rounded-full border border-green-100">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {report && (
              <>
                <BDExportReport report={report} city={cityName} period={periodLabel} />
                <BDExecutiveSummary report={report} city={cityName} period={periodLabel} />
                <BDCityMetricsDashboard report={report} />
                <BDReportSections report={report} />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1 space-y-4">
            {/* Quick Stats */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">🇧🇩 Bangladesh Market Snapshot</p>
              <div className="space-y-3">
                {[
                  { label: 'Active Listings', value: '24,500+', icon: 'Home', color: 'green' },
                  { label: 'Avg Transaction', value: '৳1.2 কোটি', icon: 'DollarSign', color: 'blue' },
                  { label: 'Monthly Volume', value: '5,800+', icon: 'TrendingUp', color: 'purple' },
                  { label: 'RAJUK Verified', value: '68%', icon: 'ShieldCheck', color: 'orange' },
                ]?.map((stat) => (
                  <div key={stat?.label} className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-${stat?.color}-50 rounded-lg flex items-center justify-center`}>
                      <Icon name={stat?.icon} size={14} className={`text-${stat?.color}-600`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{stat?.label}</p>
                      <p className="text-sm font-bold text-gray-900">{stat?.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* City Coverage */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">City Coverage</p>
              <div className="space-y-1.5">
                {[
                  { name: 'Dhaka', bn: 'ঢাকা', listings: '12,800', flag: '🏙️' },
                  { name: 'Chittagong', bn: 'চট্টগ্রাম', listings: '5,200', flag: '⚓' },
                  { name: 'Sylhet', bn: 'সিলেট', listings: '2,900', flag: '🍃' },
                  { name: 'Rajshahi', bn: 'রাজশাহী', listings: '1,800', flag: '🌾' },
                  { name: 'Khulna', bn: 'খুলনা', listings: '1,700', flag: '🌊' },
                ]?.map((c) => (
                  <div key={c?.name} className="flex items-center justify-between py-1">
                    <span className="text-sm text-gray-700">{c?.flag} {c?.name} <span className="text-xs text-gray-400">{c?.bn}</span></span>
                    <span className="text-xs text-gray-400">{c?.listings}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RAJUK Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ShieldCheck" size={14} className="text-green-600" />
                <p className="text-xs font-semibold text-green-800">RAJUK Compliance</p>
              </div>
              <p className="text-xs text-green-700 leading-relaxed mb-2">
                রাজধানী উন্নয়ন কর্তৃপক্ষ — All reports include RAJUK regulatory updates and building approval status.
              </p>
              <button
                onClick={() => window.open('http://www.rajuk.gov.bd', '_blank')}
                className="text-xs text-green-700 font-medium hover:text-green-900 flex items-center gap-1"
              >
                <Icon name="ExternalLink" size={11} />
                Visit RAJUK Portal
              </button>
            </div>

            <BDHistoricalReports onLoadReport={handleLoadHistorical} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BangladeshMarketIntelligenceReports;
